<script setup lang="ts">
import {
	computed,
	onMounted,
	ref,
	toRefs,
	ShallowRef,
	provide,
	shallowRef,
	watch,
} from "vue";
import { useExplorerStore } from "@/modules/explorer/store";
import { GRAPH_TYPES } from "chartjs-graph";
import Graph from "@/modules/explorer/components/Graphs/Graph.vue";
import dayjs from "dayjs";
import formatDateRangeForChartTitle from "@/modules/explorer/composables/formatDateRangeForChartTitle";
import { computedAsync } from "@vueuse/core";
import { storeToRefs } from "pinia";

import GraphTotals from "@/modules/explorer/components/Graphs/GraphTotals.vue";
import DocumentDO = App.Data.Explorer.DocumentDO;
import {
	derivedDocumentKey,
	documentKey,
} from "@/modules/explorer/ExplorerUtils";
import { usePropertyStore } from "@/modules/explorer/PropertyStore";
import DownloadGraphImageMenu from "@/modules/explorer/components/downloadGraphImageMenu.vue";
import {
	explorerGraphKey,
	useDownloadGraph,
} from "@/modules/explorer/components/Graphs/store";
import DataViewTabs from "@/modules/explorer/components/DataViewTabs.vue";
import OverwriteColorsDialog from "@/modules/explorer/components/Graphs/OverwriteColorsDialog.vue";
import { ApiError, Result } from "@/api/base";
import { promptInvalidPermissions } from "@/api/helpers/loadingWrapper";
import { useApiClient } from "@/api/client";
import { useAbortController } from "@/modules/explorer/composables/useAbortController";
const { abortController, abortRequestAndResetController } =
	useAbortController();
import { processedDocument } from "chartjs-graph";
import AiExplorerDrawer from "@/modules/explorer/components/SingleMetric/AiExplorerDrawer.vue";
import {
	graphIdentityKey,
	graphNeedsDataRefresh,
} from "@/modules/explorer/composables/graphDisplaySettings";

const derivedDocument: ShallowRef<DocumentDO> = shallowRef();

const explorerStore = useExplorerStore();
const { explorerState } = storeToRefs(explorerStore);
const { lastUpdated } = toRefs(explorerState.value);

const document = computed(() => explorerStore.document);

provide(derivedDocumentKey, derivedDocument);
provide(documentKey, document);

watch(derivedDocument, () => {
	useExplorerStore().derivedDocument = derivedDocument.value;
});

const graphTypes = ref([
	{
		icon: "mdi-chart-bar-stacked",
		title: "bar-chart",
		value: GRAPH_TYPES.BAR,
	},
	{
		icon: "mdi-chart-line-stacked",
		title: "area-chart",
		value: GRAPH_TYPES.AREA,
	},
	{
		icon: "mdi-chart-line",
		title: "line-chart",
		value: GRAPH_TYPES.LINE,
	},
]);

const isTeleportContainerRendered = ref(false);
const isDownloadGraphImageMenuVisible = ref(false);
const isOverwriteColorsDialogVisible = ref(false);
const isLoading = ref(true);

const annotatedEvents = computedAsync(async () => {
	await explorerStore.fetchAnnotations();
	return explorerStore.explorerState.annotations;
}, []);

const chartTitle = computed(() => {
	return formatDateRangeForChartTitle(
		(explorerStore.derivedDocument || explorerStore.document).period_selection
	);
});

onMounted(async () => {
	await usePropertyStore().loadProperties("is_configurable_on_axis");

	setTimeout(() => {
		isTeleportContainerRendered.value = true;
	}, 100);
});

const annotationsTableHeaders = [
	{ title: "Annotation Category", key: "category", sortable: false },
	{ title: "Title", key: "title", sortable: false },
	{ title: "From", key: "from", sortable: false },
	{ title: "To", key: "to", sortable: false },
	{ title: "Subject Date", key: "applies_to_subject_date", sortable: false },
	{ title: "Snapshot Date", key: "applies_to_measure_date", sortable: false },
];
const formatDate = (str) => dayjs(str).format("DD MMM YYYY");

const openDownloadGraphAsImageMenu = () => {
	isDownloadGraphImageMenuVisible.value = true;
};

let lastFetchedKey: string | null = null;

const needsDataRefresh = (): boolean =>
	graphNeedsDataRefresh(document.value, lastFetchedKey);

const fetchDataForGraph = async (): Promise<Result<any, ApiError>> => {
	abortRequestAndResetController();

	const fetchedKey = graphIdentityKey(document.value);

	const response = await promptInvalidPermissions(
		useApiClient("explorer").fetchDataForDocument(
			processedDocument(document.value),
			"explorer/",
			abortController.value?.signal ?? null
		)
	);

	if (response.isOk()) {
		lastFetchedKey = fetchedKey;
	}

	return response;
};

/**
 * Persist a display-only change (e.g. custom boundaries, legend, labels) to the
 * document history without re-querying data, so it survives a page reload.
 */
const logDisplayOnlyChange = (): void => {
	void useApiClient("explorer").logDocument(
		processedDocument(document.value),
		"explorer/"
	);
};

useDownloadGraph(explorerGraphKey);
</script>

<template>
	<teleport defer to="#download-button">
		<v-btn
			v-tooltip="'Download graph as image'"
			icon="fa: fa-solid fa-download"
			variant="text"
			size="small"
			@click="openDownloadGraphAsImageMenu"
			color="on-surface"
			data-testid="download-graph"
		/>
	</teleport>
	<overwrite-colors-dialog
		v-if="isOverwriteColorsDialogVisible"
		v-model="isOverwriteColorsDialogVisible"
		:is-loading="isLoading"
	/>
	<download-graph-image-menu
		v-if="isDownloadGraphImageMenuVisible"
		v-model="isDownloadGraphImageMenuVisible"
		:download-key="explorerGraphKey"
		:chart-title="chartTitle"
	/>

	<v-overlay
		v-model="isLoading"
		:close-on-content-click="false"
		class="align-center justify-center"
		persistent
		:scrim="false"
		contained
	>
		<div style="width: 400px">
			<v-progress-linear
				close-delay="0"
				color="primary"
				height="6"
				indeterminate
				open-delay="0"
				rounded
			></v-progress-linear>
			<div class="d-flex pt-10">
				<v-spacer />
				<v-btn variant="text" @click="isLoading = false">Close</v-btn>
				<v-spacer />
			</div>
		</div>
	</v-overlay>

	<v-card
		class="h-100 d-flex flex-column"
		variant="elevated"
		style="position: relative; overflow: hidden"
	>
		<AiExplorerDrawer v-model="explorerState.showAiDrawer" absolute />

		<div class="d-flex flex-row justify-space-between">
			<div class="d-flex px-4 pt-4">
				<GraphTotals
					v-if="document"
					:last-updated="lastUpdated"
					:is-graph-loading="isLoading"
					:needs-data-refresh="needsDataRefresh"
				/>
				<div class="pt-2">
					<span v-html="chartTitle"></span>
				</div>
			</div>
			<div class="d-flex flex-row align-start px-2 pt-2">
				<Transition name="slide-fade" appear>
					<div
						class="d-flex align-center px-2"
						v-if="useExplorerStore()?.document?.graph_type"
					>
						<v-btn-toggle
							v-model="useExplorerStore().document.graph_type"
							divided
							style="height: 29px"
							variant="outlined"
							mandatory
						>
							<v-btn
								v-for="graphType in graphTypes"
								:key="graphType.value"
								icon
								:value="graphType.value"
								size="small"
							>
								<v-icon> {{ graphType.icon }} </v-icon>
							</v-btn>
						</v-btn-toggle>
						<v-btn
							class="ml-2"
							style="border: 1px solid #dadde4; border-radius: 4px !important"
							height="29"
							v-tooltip="'Overwrite colors'"
							icon
							variant="text"
							size="small"
							color="on-surface"
							rounded="0"
							:disabled="!derivedDocument"
							:active="derivedDocument?.is_color_customized"
							@click="isOverwriteColorsDialogVisible = true"
						>
							<v-icon icon="fa: fa-light fa-paint-roller" />
						</v-btn>
					</div>
				</Transition>
				<data-view-tabs />
			</div>
		</div>

		<div class="px-4 pb-2 flex-grow-1">
			<Graph
				@update:is-loading="(value) => (isLoading = value)"
				@update:derived-document="(value:DocumentDO) => (derivedDocument = value)"
				:last-updated="lastUpdated.getTime()"
				:fetch-data-for-graph="fetchDataForGraph"
				:needs-data-refresh="needsDataRefresh"
				:log-display-only-change="logDisplayOnlyChange"
				:is-explorer="true"
				v-if="document"
				:download-key="explorerGraphKey"
			/>
		</div>
		<v-fade-transition>
			<div v-if="annotatedEvents.length > 0">
				<v-divider />
				<v-sheet color="rgba(var(--color-background), .6)" class="py-1">
					<perfect-scrollbar ref="scrollContent" style="max-height: 95px">
						<v-data-table
							class="annotations"
							style="background-color: unset"
							:headers="annotationsTableHeaders"
							:items="annotatedEvents"
							density="compact"
						>
							<template v-slot:item.category="{ value }">
								<v-chip
									v-if="value"
									:color="value.rgb_color_value"
									label
									:prepend-icon="`fa: ${value.icon}`"
									density="compact"
									:key="value.id"
									>{{ value.name }}
								</v-chip>
								<v-icon v-else>mdi-minus</v-icon>
							</template>

							<template v-slot:item.title="{ item }">
								<v-tooltip :text="item.description" location="right">
									<template v-slot:activator="{ props }">
										<span v-bind="props">{{ item.title }}</span>
									</template>
								</v-tooltip>
							</template>

							<template v-slot:item.from="{ value }"
								>{{ formatDate(value) }}
							</template>
							<template v-slot:item.to="{ value }">{{
								formatDate(value)
							}}</template>

							<template v-slot:item.applies_to_subject_date="{ value }">
								<v-icon v-if="value">mdi-check</v-icon>
								<v-icon v-else>mdi-minus</v-icon>
							</template>

							<template v-slot:item.applies_to_measure_date="{ value }">
								<v-icon v-if="value">mdi-check</v-icon>
								<v-icon v-else>mdi-minus</v-icon>
							</template>

							<template v-slot:bottom></template>
						</v-data-table>
					</perfect-scrollbar>
				</v-sheet>
			</div>
		</v-fade-transition>
	</v-card>
</template>

<style lang="scss">
.annotations {
	tr.v-data-table__tr {
		transition: background-color 0.1s ease-in-out;
	}

	tr.v-data-table__tr:hover {
		background-color: rgba(255, 255, 255, 0.7);
	}
}

.slide-fade-enter-active {
	transition: all 0.5s ease;
}

.slide-fade-leave-active {
	transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
	transform: translateX(20px);
	opacity: 0;
}
.card-drawer {
	top: 0 !important;
	height: 100% !important;
}
</style>
